<template>
  <div>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.name.label')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('name')"
    >
      <InputTextV2
        v-model="internalValue.name"
        type="text"
        :placeholder="$t('contestEntryUserForm.name.placeholder')"
        :maxlength="100"
        :isDisplayCount="!isConfirm"
        :readonly="isConfirm"
        :error="!!errorMessage('name')"
      />
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.nameKana.label')"
      :description="isConfirm ? undefined : $t('contestEntryUserForm.nameKana.description')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('nameKana')"
    >
      <InputTextV2
        v-model="internalValue.nameKana"
        type="text"
        :placeholder="$t('contestEntryUserForm.nameKana.placeholder')"
        :maxlength="100"
        :isDisplayCount="!isConfirm"
        :readonly="isConfirm"
        :error="!!errorMessage('nameKana')"
      />
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.penname.label')"
      :required="isConfirm || isEdit ? undefined : true"
      :description="isConfirm ? undefined : $t('contestEntryUserForm.penname.description')"
      :error="errorMessage('penname')"
    >
      <template v-if="!isEdit">
        <SelectBoxV2
          v-model="internalValue.penname"
          :options="pennameOptions"
          :placeholder="$t('contestEntryUserForm.penname.placeholder')"
          :readonly="isConfirm"
          :error="!!errorMessage('penname')"
        />
      </template>
      <template v-else>
        <div class="tag-list" v-if="contestUser">
          <TagItem
            v-for="penname in contestUser.penname"
            :key="penname"
            :text="penname"
            :readonly="isConfirm"
            @delete="onClickDeletePenname(penname)"
          />
        </div>
        <div v-else class="no-create">{{ $t("contestEntryUserForm.penname.notCreated") }}</div>
      </template>
      <button v-if="!isConfirm" class="add-penname" @click="onClickAddPenname">
        {{ $t("contestEntryUserForm.penname.add") }}
      </button>
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.email.label')"
      :description="isConfirm || isEdit ? undefined : $t('contestEntryUserForm.email.description')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('email')"
    >
      <InputTextV2
        v-model="internalValue.email"
        type="email"
        :initialValue="cognitoUserEmail"
        :placeholder="$t('contestEntryUserForm.email.placeholder')"
        :maxlength="100"
        :isDisplayCount="!isConfirm"
        :readonly="isConfirm"
        :error="!!errorMessage('email')"
      />
    </ContestEntryItem>
    <ContestEntryItem
      v-if="internalValue.email !== cognitoUserEmail && !isConfirm && !isEdit"
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.emailConfirm.label')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('emailConfirm')"
    >
      <InputTextV2
        v-model="internalValue.emailConfirm"
        type="email"
        :placeholder="$t('contestEntryUserForm.emailConfirm.placeholder')"
        :maxlength="100"
        :isDisplayCount="!isConfirm"
        :readonly="isConfirm"
        :error="!!errorMessage('emailConfirm')"
      />
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.phone.label')"
      :description="isConfirm ? undefined : $t('contestEntryUserForm.phone.description')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('phone')"
    >
      <InputTextV2
        v-model="internalValue.phone"
        type="tel"
        :placeholder="$t('contestEntryUserForm.phone.placeholder')"
        :maxlength="11"
        :isDisplayCount="!isConfirm"
        :readonly="isConfirm"
        :error="!!errorMessage('phone')"
      />
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.gender.label')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('gender')"
    >
      <SelectBoxV2
        v-model="internalValue.gender"
        :options="genderOptions"
        :placeholder="$t('contestEntryUserForm.gender.placeholder')"
        :allowUnselected="isEdit"
        :readonly="isConfirm"
        :error="!!errorMessage('gender')"
      />
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.birthdate.label')"
      :required="isConfirm || isEdit ? undefined : true"
      :error="errorMessage('birthdate')"
    >
      <DatePicker
        v-model="internalValue.birthdate"
        :format="'yyyy/MM/dd'"
        :language="datePickerLanguage"
        :placeholder="$t('contestEntryUserForm.birthdate.placeholder')"
        typeable
        :disabled="isConfirm"
        :inputClass="{ 'input-wrapper': true, 'date-picker': true, error: !!errorMessage('birthdate') }"
      />
    </ContestEntryItem>
    <ContestEntryItem
      class="contest-entry-item"
      :name="$t('contestEntryUserForm.historyOfAwards.label')"
      :description="isConfirm ? undefined : $t('contestEntryUserForm.historyOfAwards.description')"
      :required="isConfirm || isEdit ? undefined : false"
      :error="errorMessage('historyOfAwards')"
    >
      <InputTextV2
        v-model="internalValue.historyOfAwards"
        :rows="5"
        :placeholder="$t('contestEntryUserForm.historyOfAwards.placeholder')"
        :maxlength="500"
        :isDisplayCount="!isConfirm"
        :readonly="isConfirm"
        :error="!!errorMessage('historyOfAwards')"
      />
    </ContestEntryItem>
    <template v-if="!isEdit">
      <ContestEntryItem
        v-if="!isConfirm && contest"
        class="contest-entry-item"
        :name="$t('contestEntryUserForm.terms.label')"
        :description="isConfirm ? undefined : $t('contestEntryUserForm.terms.description')"
        :required="isConfirm ? undefined : true"
      >
        <InputTextV2 :rows="20" :value="contest.terms" readonly />
        <div class="input-wrapper terms">
          <CheckboxItem :text="$t('contestEntryUserForm.terms.agree')" v-model="internalValue.isAgreedTerms" />
        </div>
      </ContestEntryItem>
      <ContestEntryItem
        class="contest-entry-item"
        :name="$t('contestEntryUserForm.saveInfo.label')"
        :description="isConfirm ? undefined : $t('contestEntryUserForm.saveInfo.description')"
        :required="isConfirm ? undefined : false"
      >
        <div class="input-wrapper" :class="{ readonly: isConfirm }">
          <CheckboxItem
            v-if="!isConfirm"
            :text="$t('contestEntryUserForm.saveInfo.checkbox')"
            v-model="internalValue.isSaveEntryUser"
          />
          <div v-else>
            {{
              internalValue.isSaveEntryUser
                ? $t("contestEntryUserForm.saveInfo.willSave")
                : $t("contestEntryUserForm.saveInfo.willNotSave")
            }}
          </div>
        </div>
      </ContestEntryItem>
    </template>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import DatePicker from "vuejs-datepicker";
import { ja, en, ko, zh } from "vuejs-datepicker/dist/locale";
import ContestEntryItem from "@/components/molecules/ContestEntryItem.vue";
import InputTextV2 from "@/components/atoms/InputTextV2.vue";
import SelectBoxV2, { Option } from "@/components/atoms/SelectBoxV2.vue";
import CheckboxItem from "@/components/atoms/CheckboxItem.vue";
import TagItem from "@/components/atoms/TagItem.vue";
import { Gender, NolaContest, NolaContestUser } from "@/lib/models/nolaContest";
import { Dialog } from "@/lib/utils";
import InputDialog, { InputDialogProps } from "@/components/ui/dialogs/InputDialog.vue";
import BreakingChangeConfirm, { BreakingChangeConfirmProps } from "@/components/ui/dialogs/BreakingChangeConfirm.vue";
import deepEqual from "deep-equal";
import { ValidationError } from "@/pages/EventContestEntry.vue";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ContestEntryItem,
    InputTextV2,
    SelectBoxV2,
    CheckboxItem,
    TagItem,
    DatePicker,
  },
  props: {
    value: Object,
    initialValue: {
      type: Object as PropType<object>,
      default: () => ({}),
      required: false,
    },
    contest: {
      type: Object as PropType<NolaContest>,
      required: false,
    },
    cognitoUserEmail: String as PropType<string>,
    isConfirm: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isEdit: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    errors: {
      type: Array as PropType<ValidationError[]>,
      default: () => [],
      required: false,
    },
  },
  data() {
    return {};
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    contestUser() {
      return this.$store.getters["nolaContestModule/user"];
    },
    datePickerLanguage() {
      const { locale } = this.$i18n;
      if (locale === "ja") return ja;
      if (locale === "ko") return ko;
      if (locale === "zh-CN" || locale === "zh-TW") return zh;
      return en;
    },
    genderOption() {
      return (id) => this.genderOptions.find((option) => option.id === id)!;
    },
    genderOptions() {
      return Object.entries(Gender)
        .filter(([_, value]) => !Number.isNaN(Number(value)))
        .map(([key, value]) => ({
          id: value,
          name: this.$t(`common.gender.${key}`),
        }));
    },
    pennameOptions() {
      const { contestUser } = this;

      if (!contestUser || !contestUser.penname) return [];
      return (contestUser.penname as string[]).map((user) => ({
        id: user,
        name: user,
      }));
    },
    errorMessage() {
      return (type) => {
        const error = this.errors.find((error) => error.path === type);
        return error && error.message;
      };
    },
  },
  methods: {
    onClickAddPenname() {
      const dialog = new Dialog(InputDialog);
      const data: InputDialogProps = {
        title: this.$t("contestEntryUserForm.penname.addDialog.title") as string,
        close: this.$t("common.cancel") as string,
        submit: this.$t("common.create") as string,
        submitHandler: (value: string) => {
          const penname = [...((this.contestUser && this.contestUser.penname) ?? []), value];
          const payload: NolaContestUser = {
            ...this.contestUser,
            penname,
          };
          this.$store.dispatch("nolaContestModule/updateUser", payload);

          this.internalValue.pennames = penname;
        },
      };
      dialog.show(data);
    },
    async onClickDeletePenname(value) {
      const confirmDialog = new Dialog(BreakingChangeConfirm);
      const data: BreakingChangeConfirmProps = {
        title: this.$t("contestEntryUserForm.penname.deleteDialog.title") as string,
        content: this.$t("contestEntryUserForm.penname.deleteDialog.content", { name: value }) as string,
        change: this.$t("common.delete") as string,
      };
      const result = await confirmDialog.show(data);

      if (!result) {
        return;
      }

      const penname = (this.contestUser!.penname as string[]).filter((x) => x !== value);
      const payload: NolaContestUser = {
        ...this.contestUser,
        penname,
      };
      this.$store.dispatch("nolaContestModule/updateUser", payload);

      this.internalValue.pennames = penname;
    },
  },
  watch: {
    contestUser: {
      handler(contestUser: NolaContestUser) {
        // NOTE: 保存済みの応募者情報がある場合は、初期値として設定する
        if (deepEqual(this.value, this.initialValue)) {
          let formattedContestUser: { [key: string]: any } = {
            email: this.cognitoUserEmail,
            ...this.initialValue,
          };

          if (contestUser) {
            formattedContestUser = {
              ...formattedContestUser,
              name: contestUser.name,
              nameKana: contestUser.nameKana,
              email: contestUser.email ?? this.cognitoUserEmail,
              phone: contestUser.phone,
              gender: contestUser.gender && this.genderOption(contestUser.gender),
              birthdate: contestUser.birthdate,
              historyOfAwards: contestUser.historyOfAwards,
            };
          }

          this.$emit("input", formattedContestUser);
        }
      },
      deep: true,
      immediate: true,
    },
  },
});

interface Props {
  value: { [key: string]: any };
  initialValue: { [key: string]: any };
  contest?: NolaContest;
  cognitoUserEmail: string;
  isConfirm: boolean;
  isEdit: boolean;
  errors: ValidationError[];
}

interface Data {}

interface Computed {
  internalValue: { [key: string]: any };
  contestUser: NolaContestUser | null;
  datePickerLanguage: typeof ja;
  genderOption(id: Gender): Option;
  genderOptions: Option[];
  pennameOptions: Option[];
  errorMessage(type: string): string | undefined;
}

interface Methods {
  onClickAddPenname(): void;
  onClickDeletePenname(penname: string): Promise<void>;
}
</script>

<style lang="scss" scoped>
.contest-entry-item {
  margin-bottom: 20px;
}

::v-deep .input-wrapper {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-xs);
  padding: 10px 20px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);

  &::placeholder {
    color: var(--color-text-low);
  }

  &:disabled,
  &.readonly {
    border-color: var(--color-border);
    pointer-events: none;
    color: var(--color-text-normal);
    background-color: var(--color-surface-muted);
  }

  &.error {
    border-color: var(--color-warning);
  }

  &.date-picker {
    width: 220px;
  }

  &.terms {
    margin-top: 1rem;
  }
}

button {
  cursor: pointer;

  &.add-penname {
    margin-top: 10px;
    color: var(--color-text-low);
    background-color: inherit;
    border: none;
    width: fit-content;
  }
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 8px;
}

.no-create {
  color: var(--color-text-low);
}
</style>
